/*
  Home dot navigation, category strip progress, card photo indicator and soft motion.
  Plain script, no dependencies. Motion parts run only with html.menam-motion and
  when the visitor has not asked for reduced motion.
*/
(() => {
  const root = document.documentElement;
  const motionOn =
    root.classList.contains('menam-motion') && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const mobile = window.matchMedia('(max-width: 749px)');

  /* ---------- Panel reveal and photo settle ---------- */
  function setupReveal() {
    const panels = document.querySelectorAll('.editorial-panel, .menam-instagram');
    if (!panels.length) return;

    panels.forEach((panel) => {
      let i = 0;
      panel.querySelectorAll('.editorial-panel__heading, .editorial-panel__color-heading, .editorial-panel__color-text, .editorial-panel__button, .menam-instagram__heading, .menam-instagram__button').forEach((el) => {
        el.setAttribute('data-menam-reveal', '');
        el.style.setProperty('--menam-i', String(i++));
      });
    });

    if (!motionOn || !('IntersectionObserver' in window)) {
      panels.forEach((p) => p.classList.add('is-in'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.2 }
    );
    panels.forEach((p) => io.observe(p));
    root.classList.add('menam-motion-ready');
  }

  /* ---------- Header offset: sections align right under the visible header ---------- */
  const header = () => document.querySelector('#header-component');

  function headerOffset() {
    const h = header();
    return h ? Math.round(h.getBoundingClientRect().height) : 0;
  }

  function setupHeaderOffset() {
    const update = () => root.style.setProperty('--menam-header-offset', `${headerOffset()}px`);
    update();
    const h = header();
    if (h && 'ResizeObserver' in window) new ResizeObserver(update).observe(h);
    window.addEventListener('resize', update);
  }

  /* While a dot navigation runs, keep the header shown so the offset stays the same */
  function holdHeader() {
    const h = header();
    if (!h) return () => {};
    const mode = h.getAttribute('sticky');
    if (mode !== 'scroll-up') return () => {};
    h.setAttribute('sticky', 'always');
    h.dataset.stickyState = 'active';
    return () => {
      h.setAttribute('sticky', mode);
      h.dataset.stickyState = 'active';
      h.dataset.scrollDirection = 'none';
    };
  }

  function waitForScrollEnd(maxMs = 2500) {
    return new Promise((resolve) => {
      let timer = 0;
      const done = () => {
        window.removeEventListener('scroll', onScroll);
        window.clearTimeout(limit);
        resolve();
      };
      const onScroll = () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(done, 140);
      };
      const limit = window.setTimeout(done, maxMs);
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    });
  }

  /* ---------- Dot navigation on the home page (mobile) ---------- */
  function setupDots() {
    if (!document.querySelector('main[data-template="index"]')) return;

    const nav = document.createElement('nav');
    nav.className = 'menam-dots';
    nav.setAttribute('aria-label', 'Bölümler');
    document.body.appendChild(nav);

    let targets = [];
    let observer = null;
    let navigating = false;

    const labelFor = (el, index) => {
      const named = el.matches('[data-menam-dot-label]') ? el : el.querySelector('[data-menam-dot-label]');
      if (named) return named.getAttribute('data-menam-dot-label');
      // A product strip is named by its own title, wherever it sits (above or below the cards)
      const heading =
        el.querySelector('.section-resource-list__header :is(h1, h2, h3)') ||
        el.querySelector('.editorial-panel__heading, .editorial-panel__color-heading, .menam-duo__heading, h2, h3');
      const img = heading ? heading.querySelector('img[alt]') : null;
      const text = heading ? heading.textContent.trim() || (img ? img.alt.trim() : '') : '';
      return text ? text : `Bölüm ${index + 1}`;
    };

    const setActive = (index) => {
      nav.querySelectorAll('button').forEach((b, i) => b.setAttribute('aria-current', String(i === index)));
    };

    const topFor = (el, index) => {
      if (index === 0) return 0;
      return Math.max(0, Math.round(el.getBoundingClientRect().top + window.scrollY - headerOffset()));
    };

    const goTo = async (el, index) => {
      navigating = true;
      setActive(index);
      const release = holdHeader();
      const behavior = motionOn ? 'smooth' : 'auto';
      window.scrollTo({ top: topFor(el, index), behavior });
      await waitForScrollEnd();
      // Late layout changes (lazy images, header height) are corrected once
      const target = topFor(el, index);
      if (Math.abs(window.scrollY - target) > 2) {
        window.scrollTo({ top: target, behavior: 'auto' });
        await waitForScrollEnd(600);
      }
      release();
      navigating = false;
    };

    const observe = () => {
      observer?.disconnect();
      const off = headerOffset();
      // A one-pixel line right under the header: the section crossing it is the active one
      observer = new IntersectionObserver(
        (entries) => {
          if (navigating) return;
          for (const entry of entries) {
            if (entry.isIntersecting) setActive(targets.indexOf(entry.target));
          }
        },
        { rootMargin: `-${off + 1}px 0px -${Math.max(0, window.innerHeight - off - 3)}px 0px` }
      );
      targets.forEach((el) => observer.observe(el));
    };

    const build = () => {
      observer?.disconnect();
      nav.textContent = '';
      root.classList.toggle('menam-dots-on', mobile.matches);
      if (!mobile.matches) {
        nav.hidden = true;
        return;
      }
      nav.hidden = false;
      const main = document.querySelector('main');
      targets = [...main.querySelectorAll('.menam-hero, .menam-tek-panel, .editorial-panel, .menam-duo, .shopify-section:has(.section-resource-list), .shopify-section:has(.menam-instagram), .menam-kolaj')].filter(
        (el) => el.offsetParent !== null && el.getBoundingClientRect().height > 0
      );
      targets.sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);
      targets.forEach((el) => el.classList.add('menam-snap'));

      targets.forEach((el, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('aria-label', labelFor(el, index));
        button.addEventListener('click', () => goTo(el, index));
        nav.appendChild(button);
      });

      setActive(0);
      observe();
    };

    build();
    mobile.addEventListener('change', build);
    let resizeTimer = 0;
    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(observe, 200);
    });
    window.menamDots = { targets: () => targets, goTo, headerOffset };
  }

  /* ---------- Desktop horizontal menu: dropdown panels ---------- */
  function setupDesktopNav() {
    const nav = document.querySelector('.menam-nav');
    const toggles = [...document.querySelectorAll('.menam-nav__toggle')];
    if (!nav || !toggles.length) return;
    const hoverable = window.matchMedia('(hover: hover) and (pointer: fine)');
    let openTimer = 0;
    let closeTimer = 0;

    const panelOf = (t) => document.getElementById(t.getAttribute('aria-controls'));
    const isOpen = (t) => t.getAttribute('aria-expanded') === 'true';
    const close = (t, focus = false) => {
      if (!t || !isOpen(t)) return;
      t.setAttribute('aria-expanded', 'false');
      const p = panelOf(t);
      if (p) p.hidden = true;
      if (focus) t.focus();
    };
    const closeAll = (except) => toggles.forEach((t) => t !== except && close(t));
    const open = (t) => {
      closeAll(t);
      t.setAttribute('aria-expanded', 'true');
      const p = panelOf(t);
      if (p) p.hidden = false;
    };
    const clearTimers = () => {
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);
    };

    // Every top item, with or without a panel: moving onto another item switches at once;
    // a short delay only for the first opening, so a pointer passing over the bar opens nothing
    nav.querySelectorAll('.menam-nav__item').forEach((item) => {
      const t = item.querySelector('.menam-nav__toggle');
      item.addEventListener('mouseenter', () => {
        if (!hoverable.matches) return;
        clearTimers();
        if (!t) {
          closeAll();
          return;
        }
        if (isOpen(t)) return;
        if (toggles.some(isOpen)) open(t);
        else openTimer = window.setTimeout(() => open(t), 160);
      });
      if (!t) return;
      t.addEventListener('click', () => {
        clearTimers();
        isOpen(t) ? close(t) : open(t);
      });
      item.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') close(t, true);
      });
      item.addEventListener('focusout', (event) => {
        if (!item.contains(/** @type {Node} */ (event.relatedTarget))) close(t);
      });
    });

    // Leaving the whole menu (bar and open panel) closes it after a moment
    nav.addEventListener('mouseleave', () => {
      if (!hoverable.matches) return;
      clearTimers();
      closeTimer = window.setTimeout(() => closeAll(), 220);
    });
    nav.addEventListener('mouseenter', () => window.clearTimeout(closeTimer));

    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element) || !event.target.closest('.menam-nav')) closeAll();
    });
  }

  /* ---------- Category strip progress line ---------- */
  function setupStripProgress() {
    document.querySelectorAll('.menam-subcollections').forEach((strip) => {
      const bar = strip.parentElement?.querySelector('.menam-subcollections__progress');
      if (!bar) return;
      const update = () => {
        const max = strip.scrollWidth - strip.clientWidth;
        const trackWidth = bar.clientWidth;
        const ratio = strip.scrollWidth ? strip.clientWidth / strip.scrollWidth : 1;
        const width = Math.max(24, trackWidth * ratio);
        const x = max > 0 ? (strip.scrollLeft / max) * (trackWidth - width) : 0;
        bar.style.setProperty('--menam-progress-width', `${width}px`);
        bar.style.setProperty('--menam-progress-x', `${x}px`);
        bar.hidden = max <= 0;
      };
      strip.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ---------- Product card: which photo is showing (mobile) ---------- */
  function setupCardProgress(scope = document) {
    scope.querySelectorAll('.card-gallery').forEach((gallery) => {
      if (gallery.querySelector('.menam-card-progress')) return;
      const slides = gallery.querySelector('slideshow-slides');
      const count = slides ? slides.querySelectorAll(':scope > slideshow-slide').length : 0;
      if (!slides || count < 2) return;
      const line = document.createElement('div');
      line.className = 'menam-card-progress';
      line.setAttribute('aria-hidden', 'true');
      for (let i = 0; i < count; i++) line.appendChild(document.createElement('i'));
      gallery.appendChild(line);
      const update = () => {
        const index = slides.clientWidth ? Math.round(slides.scrollLeft / slides.clientWidth) : 0;
        [...line.children].forEach((dot, i) => dot.toggleAttribute('data-active', i === index));
      };
      slides.addEventListener('scroll', update, { passive: true });
      update();
    });
  }

  /* ---------- Card hover swap, menu stagger, cart bump ---------- */
  function setupInteractions() {
    if (!motionOn) return;

    document.addEventListener(
      'pointerenter',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element) || event.pointerType !== 'mouse') return;
        if (!target.classList.contains('card-gallery')) return;
        target.classList.remove('menam-fade');
        void target.offsetWidth;
        target.classList.add('menam-fade');
      },
      true
    );

    let i = 0;
    document.querySelectorAll('.menu-editorial__level-1 > li, .menu-editorial__tile').forEach((el) => {
      el.classList.add('menam-stagger');
      el.style.setProperty('--menam-i', String(i++));
    });

    document.addEventListener('cart:update', () => {
      // The cart icon itself is re-rendered after an update, so animate its wrapper a moment later
      window.setTimeout(() => {
        document.querySelectorAll('cart-icon').forEach((icon) => {
          const host = icon.parentElement;
          if (!host) return;
          host.classList.remove('menam-bump');
          void host.offsetWidth;
          host.classList.add('menam-bump');
          window.setTimeout(() => host.classList.remove('menam-bump'), 700);
        });
      }, 80);
    });
  }

  const start = () => {
    setupHeaderOffset();
    setupDesktopNav();
    setupReveal();
    setupDots();
    setupStripProgress();
    setupCardProgress();
    setupInteractions();

    // Cards added later (infinite scroll, recommendations) get the photo indicator too
    const main = document.querySelector('main');
    if (main && 'MutationObserver' in window) {
      let timer = 0;
      new MutationObserver(() => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => setupCardProgress(main), 200);
      }).observe(main, { childList: true, subtree: true });
    }
  };

  // Desktop product gallery: left and right arrow keys move the photo carousel
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    if (!window.matchMedia('(min-width: 990px)').matches) return;
    const gallery = document.querySelector('main media-gallery slideshow-component');
    if (!gallery || document.querySelector('dialog[open]')) return;
    const active = document.activeElement;
    const inGallery = !!active && gallery.contains(active);
    if (!inGallery && active && active !== document.body) return;
    const box = gallery.getBoundingClientRect();
    if (box.bottom < 0 || box.top > window.innerHeight) return;
    if (typeof gallery.next !== 'function' || typeof gallery.previous !== 'function') return;
    event.preventDefault();
    if (event.key === 'ArrowRight') gallery.next();
    else gallery.previous();
  });

  // Mobile menu: one group open at a time (fallback for browsers without exclusive <details name>)
  document.addEventListener(
    'toggle',
    (event) => {
      const group = event.target;
      if (!(group instanceof HTMLDetailsElement) || !group.open) return;
      if (!group.classList.contains('menam-drawer-nav__group')) return;
      document.querySelectorAll('.menam-drawer-nav__group[open]').forEach((other) => {
        if (other !== group) other.open = false;
      });
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
